import React, { FC, ReactElement, useEffect, useState } from "react";

import { Divider, TextField, Tooltip, useTheme } from "@mui/material";
import { useMsal } from "@azure/msal-react";

import delete_svg from "../../assets/delete.svg";
import conversation_svg from "../../assets/conversation.svg";
import { ReactComponent as ConversationIcon } from "../../assets/conversation.svg";
import { Edit } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import apiCall from "../../utils/apiCall";
import {
  DELETE_CONVERSATION,
  GET_CONV_BY_USER_ID,
  UPDATE_CONVERSATION_NAME,
} from "../../endpoint";

import { useAppDispatch, useAppSelector } from "../../hooks/reduxhook";
import {
  addConversations,
  addconversation,
  clearActiveChat,
  getconversationid,
} from "../../redux/slices/conversationSlice";
import { loadingforconversation } from "../../redux/slices/loadingSlice";
import ConversationTitle from "./ConversationTitle";

import { addType, getType } from "../../redux/slices/typeSlice";
import types from "../../data/types";
import { removedrawerOpening } from "../../redux/slices/drawerOpening";
import { addModel, getModelDetails } from "../../redux/slices/modelSlice";

import ConfirmDeleteModal from "../ConfirmDelete";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { addError } from "../../redux/slices/errorSlice";
import { getDefaultModel } from "./NewConversationButton";
import { DEFAULT_LLM_MODEL } from "../../data/constants";
interface i_conv {
  name: string | { result?: string };
  conversation_id: string;
}
const ConversationList: FC<i_conv> = ({
  name,
  conversation_id,
}): ReactElement => {
  const dispatch = useAppDispatch();
  const current_conversation_id = useAppSelector(getconversationid);
  const type = useAppSelector(getType);

  const models = useAppSelector(getModelDetails);

  const [edit, setEdit] = useState(false);
  const [newName, setNewName] = useState(name);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const { accounts } = useMsal();
  const account = accounts[0];
  const theme = useTheme();
  const extractConversationName = (name: any): string => {
    if (!name) return "";

    // already clean string
    if (typeof name === "string") {
      // looks like JSON → try parsing
      if (name.trim().startsWith("{")) {
        try {
          const parsed = JSON.parse(
            name.replace(/'/g, '"'), // handle single quotes
          );
          return parsed?.result ?? name;
        } catch {
          return name;
        }
      }
      return name;
    }

    // object case
    if (typeof name === "object") {
      return name?.result ?? "";
    }

    return "";
  };
  const displayName = extractConversationName(name);
  useEffect(() => {
    setNewName(displayName);
  }, [displayName]);
  const deleteConversation = (e: any) => {
    e.stopPropagation();
    dispatch(loadingforconversation(true));
    apiCall("POST", DELETE_CONVERSATION, {
      conv_id: conversation_id,
      user_email: account.username,
    })
      .then((res) => {
        dispatch(addConversations(res?.result));
        dispatch(clearActiveChat());
      })
      .catch((err) => {
        dispatch(addError("Failed to delete a conversation.Please try again."));
      })
      .finally(() => {
        dispatch(loadingforconversation(false));
      });
  };
  const updateConvName = () => {
    dispatch(loadingforconversation(true));
    apiCall("POST", UPDATE_CONVERSATION_NAME, {
      conv_id: conversation_id,
      user_email: account.username,
      conv_name: newName,
    })
      .then((res) => {
        dispatch(addConversations(res?.result));
        setEdit(false);
      })
      .catch((err) => {
        dispatch(addError("Failed to update a conversation.Please try agian"));
      })
      .finally(() => {
        dispatch(loadingforconversation(false));
      });
  };
  const selectConversation = async () => {
    dispatch(loadingforconversation(true));

    if (!type.type) {
      dispatch(addType(types[0]));
      dispatch(removedrawerOpening());
      dispatch(
        addModel(
          process.env?.REACT_APP_DEFAULT_LLM_MODEL ??
            getDefaultModel(models, DEFAULT_LLM_MODEL),
        ),
      );
    }

    apiCall("POST", GET_CONV_BY_USER_ID, {
      conv_id: conversation_id,
    })
      .then(async (res) => {
        const c = [
          ...res.result.map((chat: any) => [
            {
              type: "user",
              message: chat.question,
              sources: [],
            },
            {
              type: "gpt",
              message: chat.answer,
              sources: chat.sources ?? [],
              chat_type: chat.chat_type,
              model: chat.model ?? chat.llm_doc,
              cypher: chat.cypher,
              org_cypher: chat.org_cypher,
              db_creds: chat.creds,
              image_model: chat.llm_img,
              collection_chunks: chat.chunks,
              collection_images: chat?.images,
              pii: chat?.pii,
              search_type: "similarity",
            },
          ]),
        ];
        const newchat: any[] = [];
        c.forEach(async (chat) => {
          newchat.push(chat[0]);
          newchat.push(chat[1]);
        });
        dispatch(
          addconversation({
            conversation_id,
            chats: newchat,
          }),
        );
      })
      .catch((err) => {
        dispatch(
          addError("Failed to load the chat for conversation.Please try again"),
        );
      })
      .finally(() => {
        dispatch(loadingforconversation(false));
      });
  };
  const active = current_conversation_id === conversation_id;
  return (
    <>
      <div
        data-testid="chat-text"
        className={`chat-text ${active ? "chat-text--active" : ""} ${
          theme.palette.mode === "dark" ? "theme-dark" : "theme-light"
        }`}
        onClick={selectConversation}
      >
        {/* children */}
        <ConversationIcon width={20} />
        &nbsp;&nbsp;
        {edit ? (
          <>
            <TextField
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="name-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") updateConvName();
              }}
              size="small"
              onBlur={() => {
                updateConvName();
              }}
            />
          </>
        ) : displayName.length > 25 ? (
          <Tooltip title={displayName}>
            <div className="tip">
              <ConversationTitle
                title={displayName.substring(0, 20) + " ..."}
                active={active}
              />
            </div>
          </Tooltip>
        ) : (
          <ConversationTitle title={displayName} active={active} />
        )}
        {edit ? (
          <TaskAltIcon
            color="primary"
            fontSize="small"
            onClick={() => updateConvName()}
            sx={{
              mt: 0.4,
              cursor: "pointer",
            }}
          />
        ) : (
          <Tooltip title="Edit">
            <Edit
              fontSize="small"
              id="editconversation"
              data-testid="edit-icon"
              sx={{
                color: "#000",
                fontSize: "16px",
                mx: 1,
              }}
              onClick={(e) => {
                setEdit(true);
                e.stopPropagation();
              }}
            />
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <DeleteIcon
            color="primary"
            id="deleteconversation"
            data-testid="delete_conversation"
            sx={{
              alt: "Delete",
              fontSize: "18px",
              marginRight: "10px",
            }}
            onClick={(e) => {
              setShowConfirmDelete(true);
              e.stopPropagation();
            }}
          />
        </Tooltip>
      </div>
      <Divider
        sx={{
          width: "90%",
          margin: "auto",
          background: active
            ? theme.palette.primary.main
            : theme.palette.mode === "dark"
              ? "#333"
              : "#eee",
        }}
      />
      <ConfirmDeleteModal
        show={showConfirmDelete}
        cancel={() => setShowConfirmDelete(false)}
        confirm={deleteConversation}
      />
    </>
  );
};

export default ConversationList;
